import * as React from 'react';
import {
	Layout,
	Row,
	Col,
	Table,
	Card,
	Button,
	Modal,
	Tooltip,
	Form,
	Input,
	Divider,
	message,
} from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import FileUploader from '../file_uploader/FileUploader';

import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	dateFormatter,
	optionalFilter,
	capitalizeEachWord,
	checkAndFormatPhoneNumber,
} from '../../utils/DataFormatterUtils';
import Mapbox from '../mapbox/Mapbox';
import { SocialMediaBadge } from '../social_media_badge/SocialMediaBadge';
import { AddressDisplay } from '../address_display/AddressDisplay';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import { FlexibleImageAvatar } from '../flexible-image-avatar/FlexibleImageAvatar';
import { EmptyState } from '../empty_state/EmptyState';
import { SOCIAL_MEDIA_TYPES } from '../../constants/social_media';
import { FAQRowDisplay } from '../faq_row_display/FAQRowDisplay';
import ContractorSupplierRelationshipForm from './ContractorSupplierRelationshipForm';
import {
	requestFilesV2ForBuyer,
	requestFilesV2ForSupplier,
} from '../../thunks/supplier_private_networks_thunks';
import { generateBackLink } from '../../utils/HistoryUtils';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import { BUYER_SUPPLIER_MANAGER_ROLES, hasRequiredRole } from '../roles/roles';
import ContractorSupplierInformationForm from './ContractorSupplierInformationForm';
import { ROLE_TYPES } from '../../utils/DataConstants';
import ContractorSupplierContactDirectory from './ContractorSupplierContactDirectory';
import {
	ContractorPrivateNetworksRestCrudThunksForBuyer,
	ContractorPrivateNetworksRestCrudThunksForSupplier,
} from '../../thunks/contractors_private_networks_thunks';
import EditSubcontractorClientContactDetails from '../edit_subcontractor_client_contact_details/EditSubcontractorClientContactDetails';

const { Content } = Layout;

require('./ContractorSuppliersDetailPage.less');
const queryString = require('qs');

const DAYS_OF_THE_WEEK = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

const DEFAULT_BACK_LINK_TO = (userType) => `/${userType}/contractors/overview/privateNetwork`;
const DEFAULT_BACK_LINK_TEXT = 'Back to Subcontractors';

class ContractorSuppliersDetailPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			modalContent: null,
			modalVisible: false,
			supplierPrivateNetworkEditFormVisible: false,
			data: {},
			requested: [],
			updatingPrivateNetwork: false,
			inviteSupplierModalVisible: false,
			inviteLoading: false,
			showEditDetailsModal: false,
		};
		this.fetchData = this.fetchData.bind(this);
	}

	fetchData() {
		this.setState({ loading: true });
		const { supplierId, fetchSupplierRelationship, fetchSupplierFacility, match, currentUser } =
			this.props;
		const supplierFacilityId = supplierId
			? supplierId
			: match.params.id === 'me'
			? currentUser.facility.id
			: match.params.id;
		fetchSupplierRelationship(supplierFacilityId).then((supplierRelationship) => {
			if (supplierRelationship.length > 0) {
				this.setState({ loading: false, data: supplierRelationship[0] });
			} else {
				fetchSupplierFacility(supplierFacilityId).then((supplierFacility) =>
					this.setState({ loading: false, data: { supplierFacility: supplierFacility } })
				);
			}
		});
	}

	componentDidMount() {
		const { location, updateNavbar, userType } = this.props;
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);
		if (queryParams.backlinkParams) {
			const { tab, pathName, backLinkText, ...otherParams } = queryString.parse(
				queryParams.backlinkParams
			);
			const finalPath = pathName || DEFAULT_BACK_LINK_TO(userType);
			let backText = backLinkText || DEFAULT_BACK_LINK_TEXT;
			updateNavbar(backText, generateBackLink(finalPath, location));
		} else {
			updateNavbar(
				DEFAULT_BACK_LINK_TEXT,
				generateBackLink(DEFAULT_BACK_LINK_TO(userType), location)
			);
		}
		this.fetchData();
	}

	componentWillUnmount(): void {
		this.props.clearNavbar();
	}

	handleEditToggle = (toggleKey) => {
		const currentState = this.state[toggleKey];
		this.setState({ [toggleKey]: !currentState });
	};

	handleEditCancel = (toggleKey) => {
		this.setState({ [toggleKey]: false });
	};

	handleOpenCertificationModal = (certification, idx) => {
		this.setState({
			modalVisible: true,
			modalContent: (
				<div key={idx}>
					<h5>{certification.certificationType}</h5>

					<KeyValueDisplay
						keyValueStore={{
							'Certificate #': certification.number,
							Issuer: certification.issuer,
							Qualifier: certification.qualifier,
							Expiration: dateFormatter(certification.expiration, 'MMM D, YYYY'),
							Attachment: certification.certificationFile,
						}}
						renderFuncs={{
							Attachment: (file) =>
								file ? (
									<FileUploader
										roleType="supplier"
										defaultFileList={file ? [file] : []}
										uploaderType="readOnly"
									/>
								) : (
									'--'
								),
						}}
					/>
				</div>
			),
		});
	};

	handleOpenInsuranceModal = (insurance, idx) => {
		const policyDocColumns = [
			{
				title: 'Policy',
				dataIndex: 'policyName',
			},
			{
				title: 'Amount',
				dataIndex: 'amount',
				render: (text, record) => <span>${text.toLocaleString('en')}</span>,
			},
			{
				title: 'Expires',
				dataIndex: 'expiration',
				render: (text, record) => dateFormatter(text, 'MMM D, YYYY'),
			},
			{
				title: 'Attachment',
				dataIndex: 'policyFile',
				render: (text, record) =>
					text ? (
						<FileUploader roleType="supplier" defaultFileList={[text]} uploaderType="readOnly" />
					) : (
						'--'
					),
			},
		];
		this.setState({
			modalVisible: true,
			modalContent: (
				<div>
					<h5>{insurance.companyName}</h5>
					<div style={{ padding: '8px 0', display: 'flex' }}>
						<div>
							<div
								style={{
									fontSize: 16,
									marginBottom: 4,
								}}
							>
								{insurance.companyContact.name}
							</div>
							<div
								style={{
									fontSize: 14,
									color: 'rgba(0,0,0,0.45)',
								}}
							>
								{insurance.companyContact.phone}
							</div>
						</div>
						<div
							style={{
								fontSize: 14,
								marginLeft: 32,
								color: 'rgba(0,0,0,0.45)',
							}}
						>
							<AddressDisplay address={insurance.companyContact.address} />
						</div>
					</div>
					<Table pagination={false} dataSource={insurance.policyDocs} columns={policyDocColumns} />
				</div>
			),
		});
	};

	handleOpenLicenseModal = (license, idx) => {
		this.setState({
			modalVisible: true,
			modalContent: (
				<div>
					<h5 style={{ marginBottom: 16 }}>{license.licenseType}</h5>
					<KeyValueDisplay
						keyValueStore={{
							'License #': license.number,
							Issuer: license.issuer,
							Qualifier: license.qualifier,
							Expiration: dateFormatter(license.expiration, 'MMM D, YYYY'),
							Attachment: license.licenseFile,
						}}
						renderFuncs={{
							Attachment: (file) =>
								file ? (
									<FileUploader
										roleType="supplier"
										defaultFileList={file ? [file] : []}
										uploaderType="readOnly"
									/>
								) : (
									'--'
								),
						}}
					/>
				</div>
			),
		});
	};

	onUpdateSuccess = (data) => {
		this.fetchData();
	};

	updateFields = (values = {}) => {
		const { updateSupplierInPrivateNetworkV2 } = this.props;

		const dataModel = {
			...this.state.data,
			...values,
		};
		updateSupplierInPrivateNetworkV2(dataModel)
			.then((data) => {
				this.onUpdateSuccess(data);
				message.success('Details updated succesfully');
			})
			.catch((err) => {
				message.error('Something went wrong, Please try again!');
			});
	};

	handleFileManualUpload = (newAttachments) => {
		let callUpdate = newAttachments.every((element) => {
			return element && element.fileId && element.fileName;
		});
		if (callUpdate) {
			this.updateFields({ internalBuyerAttachments: newAttachments });
		}
	};
	requestFile = (type) => {
		const { currentUser, requestFiles } = this.props;
		const { data: { supplierFacility = {} } = {}, requested } = this.state;

		requestFiles({
			supplierFacilityId: supplierFacility.id,
			type,
			fromEmail: currentUser.email,
		}).then(() => {
			this.setState({ requested: [...requested, type] });
		});
	};
	setShowEditDetailsModal = (val) => {
		this.setState({ showEditDetailsModal: val });
	};

	render() {
		const { currentUser, userType } = this.props;

		const { loading, data = {}, requested } = this.state;
		if (loading) return <PageLoadingPlaceholder />;
		const {
			supplierFacility = {},
			supplierFacilityAlias,
			relationshipStatus,
			internalBuyerAttachments,
			internalBuyerNotes,
		} = data;
		const isManager = hasRequiredRole(currentUser.roles, BUYER_SUPPLIER_MANAGER_ROLES);

		const contactInfo = loading
			? {}
			: {
					Email: optionalFilter(supplierFacility.primaryContactEmail),
					Phone: optionalFilter(checkAndFormatPhoneNumber(supplierFacility.primaryPhone)),
					Address: nullSafeGet('primaryAddress', supplierFacility) ? (
						<AddressDisplay address={supplierFacility.primaryAddress} />
					) : (
						'--'
					),
					Website: optionalFilter(supplierFacility.website),
			  };

		const operatingHours = supplierFacility.operatingHours || {};

		return (
			<Content
				className="contractorSuppliersDetailPage"
				style={{ position: 'relative', padding: 0 }}
			>
				<Modal
					visible={this.state.modalVisible}
					width={600}
					onCancel={(e) => this.setState({ modalVisible: false, modalContent: null })}
					footer={null}
					closable={true}
				>
					{this.state.modalContent}
				</Modal>
				<EditSubcontractorClientContactDetails
					visible={this.state.showEditDetailsModal}
					close={() => this.setShowEditDetailsModal(false)}
					formData={supplierFacility}
					data={data}
				/>

				{loading ? (
					<PageLoadingPlaceholder />
				) : (
					<div style={{}}>
						<Row style={{ margin: '0 -8px' }} gutter={16}>
							<Col span={24}>
								<div className="contractorSuppliersDetailPage__header">
									<div style={{ float: 'left', display: 'flex', flexGrow: 1 }}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyItems: 'space-between',
												flexGrow: 1,
											}}
										>
											<div style={{ marginRight: 24 }}>
												{supplierFacility.logoURL ? (
													<FlexibleImageAvatar
														displayName={supplierFacilityAlias || supplierFacility.displayName}
														showPopover={false}
														popoverText={supplierFacilityAlias || supplierFacility.displayName}
														width="120px"
														height="120px"
														imageUrl={supplierFacility.logoURL}
													/>
												) : (
													<FlexibleImageAvatar
														displayName={supplierFacilityAlias || supplierFacility.displayName}
														showPopover={false}
														popoverText={supplierFacilityAlias || supplierFacility.displayName}
														width="120px"
														height="120px"
														imageUrl="https://s3.amazonaws.com/emptor-data-assets/images/empty-state/icons8-city-filled-100.png"
													/>
												)}
											</div>
											<div>
												<h4 style={{ color: 'white', marginBottom: 4 }}>
													{supplierFacilityAlias || supplierFacility.displayName}
													{relationshipStatus && (
														<div
															className={`contractorSuppliersDetailPage__relationshipStatus ${relationshipStatus}`}
														>
															{relationshipStatus === 'SelfManaged'
																? 'Self Managed'
																: relationshipStatus}
														</div>
													)}
												</h4>
											</div>
										</div>
									</div>
									<div
										style={{
											float: 'right',
											display: 'flex',
											justifyContent: 'flex-end',
											alignItems: 'center',
											maxWidth: 400,
										}}
									>
										{SOCIAL_MEDIA_TYPES.every(
											(sm) =>
												supplierFacility.socialMedia === undefined ||
												supplierFacility.socialMedia[sm.key] === undefined
										)
											? null
											: SOCIAL_MEDIA_TYPES.map((sm) =>
													supplierFacility.socialMedia[sm.key] !== undefined ? (
														<div style={{ marginLeft: 8 }}>
															<SocialMediaBadge
																key={sm.type}
																type={sm.type}
																handle={supplierFacility.socialMedia[sm.key]}
															/>
														</div>
													) : null
											  )}
									</div>
								</div>
							</Col>
						</Row>

						<Row style={{ margin: '16px 4px' }} gutter={16}>
							<Col span={16}>
								{supplierFacility.about && supplierFacility.about.length > 0 && (
									<Row className="rowSpacing" gutter={16}>
										<Col span={24}>
											<Card>
												<h5 style={{ display: 'flex', alignItems: 'center' }}>
													About this subcontractor
												</h5>
												<div style={{ fontSize: 18 }}>{supplierFacility.about}</div>
											</Card>
										</Col>
									</Row>
								)}
								<Row className="rowSpacing" gutter={16}>
									<Col span={24}>
										<Card>
											<ContractorSupplierInformationForm
												data={this.state.data}
												onSubmit={this.updateFields}
											/>
										</Card>
									</Col>
								</Row>
								<Row className="rowSpacing" gutter={16}>
									<Col span={24}>
										<ContractorSupplierContactDirectory
											supplier={this.state.data}
											onSucess={this.onUpdateSuccess}
										/>
									</Col>
								</Row>

								{/* {relationshipStatus && (
									<>
										<Row className="rowSpacing" gutter={16}>
											<Col span={24}>
												<Card>
													<ContractorSupplierRelationshipForm
														data={this.state.data}
														onSubmit={this.updateFields}
														allowedToEdit={isManager}
													/>
												</Card>
											</Col>
										</Row>
									</>
								)} */}

								{/* <Row className="rowSpacing" gutter={16}>
									<Col span={24}>
										<Card>
											<h5 style={{ display: 'flex', alignItems: 'center' }}>W9</h5>
											{!supplierFacility.w9 ? (
												<EmptyState
													height={120}
													headline={null}
													body={
														<div style={{ textAlign: 'center' }}>
															<div style={{ maxWidth: 488, marginBottom: 16 }}>
																This supplier hasn't uploaded a W9.
															</div>
															{isManager ? (
																<div style={{ maxWidth: 488, marginBottom: 16 }}>
																	<Button
																		disabled={requested.includes('w9')}
																		onClick={() => this.requestFile('w9')}
																	>
																		{requested.includes('w9') ? 'Requested' : 'Request'}
																	</Button>
																</div>
															) : null}
														</div>
													}
												/>
											) : (
												<FileUploader
													roleType="supplier"
													defaultFileList={supplierFacility.w9 ? [supplierFacility.w9] : []}
													uploaderType="readOnly"
												/>
											)}
										</Card>
									</Col>
								</Row>

								<Row className="rowSpacing" gutter={16}>
									<Col span={24}>
										<Card>
											<h5 style={{ display: 'flex', alignItems: 'center' }}>Insurances</h5>
											{!supplierFacility.insurances || supplierFacility.insurances.length === 0 ? (
												<EmptyState
													height={120}
													headline={null}
													body={
														<div style={{ textAlign: 'center' }}>
															<div style={{ maxWidth: 488, marginBottom: 16 }}>
																This supplier hasn't uploaded Insurance information.
															</div>
															<div style={{ maxWidth: 488, marginBottom: 16 }}>
																<Button
																	disabled={requested.includes('coi')}
																	onClick={() => this.requestFile('coi')}
																>
																	{requested.includes('coi') ? 'Requested' : 'Request'}
																</Button>
															</div>
														</div>
													}
												/>
											) : (
												<div>
													{supplierFacility.insurances.map((insurance, idx) => (
														<div key={idx}>
															<div
																style={{
																	fontSize: 18,
																	display: 'flex',
																	alignItems: 'center',
																}}
															>
																<img
																	width="80px"
																	height="80px"
																	style={{ marginRight: 16 }}
																	src="https://mock-data-assets.s3.amazonaws.com/categories/images/licenseonfile.png"
																/>
																<div>
																	<div>Insured by {insurance.companyName}</div>
																	<div>
																		<Button
																			style={{
																				paddingLeft: 0,
																				paddingRight: 0,
																			}}
																			onClick={() => this.handleOpenInsuranceModal(insurance, idx)}
																			type="link"
																		>
																			View Details
																		</Button>
																	</div>
																</div>
															</div>
														</div>
													))}
													{isManager ? (
														<div style={{ maxWidth: 488, marginBottom: 16, marginTop: 16 }}>
															<Button
																disabled={requested.includes('coi')}
																onClick={() => this.requestFile('coi')}
															>
																{requested.includes('coi') ? 'Requested' : 'Request'}
															</Button>
														</div>
													) : null}
												</div>
											)}
										</Card>
									</Col>
								</Row>

								<Row className="rowSpacing" gutter={16}>
									<Col span={24}>
										<Card>
											<h5 style={{ display: 'flex', alignItems: 'center' }}>Licenses</h5>
											{!supplierFacility.licenses || supplierFacility.licenses.length === 0 ? (
												<EmptyState
													height={120}
													headline={null}
													body={
														<div style={{ textAlign: 'center' }}>
															<div style={{ maxWidth: 488, marginBottom: 16 }}>
																This supplier hasn't uploaded any Licenses.
															</div>
															{isManager ? (
																<div style={{ maxWidth: 488, marginBottom: 16 }}>
																	<Button
																		disabled={requested.includes('license')}
																		onClick={() => this.requestFile('license')}
																	>
																		{requested.includes('license') ? 'Requested' : 'Request'}
																	</Button>
																</div>
															) : null}
														</div>
													}
												/>
											) : (
												<div>
													{supplierFacility.licenses.map((license, idx) => (
														<div key={idx}>
															<div
																style={{
																	fontSize: 18,
																	display: 'flex',
																	alignItems: 'center',
																}}
															>
																<img
																	width="80px"
																	height="80px"
																	style={{ marginRight: 16 }}
																	src="https://mock-data-assets.s3.amazonaws.com/categories/images/licenseonfile.png"
																/>
																<div>
																	<div>
																		Licensed for {license.licenseType} by {license.issuer}
																	</div>
																	<div>
																		<Button
																			style={{
																				paddingLeft: 0,
																				paddingRight: 0,
																			}}
																			onClick={() => this.handleOpenLicenseModal(license, idx)}
																			type="link"
																		>
																			View Details
																		</Button>
																	</div>
																</div>
															</div>
														</div>
													))}
													{isManager ? (
														<div style={{ maxWidth: 488, marginBottom: 16, marginTop: 16 }}>
															<Button
																disabled={requested.includes('license')}
																onClick={() => this.requestFile('license')}
															>
																{requested.includes('license') ? 'Requested' : 'Request'}
															</Button>
														</div>
													) : null}
												</div>
											)}
										</Card>
									</Col>
								</Row>

								<Row className="rowSpacing" gutter={16}>
									<Col span={24}>
										<Card>
											<h5 style={{ display: 'flex', alignItems: 'center' }}>
												Frequently asked questions
											</h5>
											{supplierFacility.faqs && supplierFacility.faqs.length > 0 ? (
												supplierFacility.faqs.map((faq, idx) => (
													<FAQRowDisplay style={{ marginBottom: 40 }} key={idx} faq={faq} />
												))
											) : (
												<EmptyState
													height={120}
													headline={null}
													body={
														<div style={{ textAlign: 'center' }}>
															<div style={{ maxWidth: 488, marginBottom: 16 }}>
																They haven't added any answers to frequently asked questions yet.
															</div>
														</div>
													}
												/>
											)}
										</Card>
									</Col>
								</Row> */}
							</Col>

							<Col span={8}>
								{/* {relationshipStatus && (
									<>
										{isManager ? (
											<Row className="rowSpacing">
												<Col span={24}>
													<Tooltip placement="top" title="Only visible to your team">
														<Card>
															<h5 style={{ display: 'flex', alignItems: 'center' }}>
																Internal Attachments
															</h5>
															{userType === ROLE_TYPES.SUPPLIER ? (
																<FileUploader
																	defaultFileList={internalBuyerAttachments || []}
																	roleType="supplier"
																	handleUploadSuccess={this.handleFileManualUpload}
																	uploaderType={'dragger'}
																	multiple
																/>
															) : (
																<FileUploader
																	defaultFileList={internalBuyerAttachments || []}
																	roleType="buyer"
																	handleUploadSuccess={this.handleFileManualUpload}
																	uploaderType={'dragger'}
																	multiple
																/>
															)}
														</Card>
													</Tooltip>
												</Col>
											</Row>
										) : null}
										<Row className="rowSpacing">
											<Col span={24}>
												<Tooltip placement="top" title="Only visible to your team">
													<Card>
														<h5 style={{ display: 'flex', alignItems: 'center' }}>
															Internal Notes
														</h5>
														<Form
															onFinish={(values) => {
																this.updateFields(values);
															}}
															initialValues={{ internalBuyerNotes }}
														>
															<Form.Item name="internalBuyerNotes">
																<Input.TextArea />
															</Form.Item>
															<Button type="primary" htmlType="submit">
																Save
															</Button>
														</Form>
													</Card>
												</Tooltip>
											</Col>
										</Row>
									</>
								)} */}
								<Row style={{ margin: '0 -8px' }} gutter={16}>
									<Col span={24}>
										<Row className="rowSpacing" gutter={16}>
											<Col span={24}>
												<Card bodyStyle={{ padding: 0 }}>
													{supplierFacility.longitude && supplierFacility.latitude ? (
														<div style={{ height: 240, position: 'relative' }}>
															<Mapbox
																longitude={supplierFacility.longitude}
																latitude={supplierFacility.latitude}
																circleRadiusInMiles={supplierFacility.radiusOfServiceInMiles}
																baseLayerStyle="light"
																zoomLevel={7}
															/>
														</div>
													) : null}
													<div className="rowSpacing">
														<Card
															title={
																<Row justify={'space-between'}>
																	<span>Contact Information</span>
																	{/*
																	<Button
																		type={'ghost'}
																		key="edit"
																		onClick={() => this.setShowEditDetailsModal(true)}
																	>
																		Edit
																	</Button>
																	*/}
																</Row>
															}
														>
															<KeyValueDisplay keyWidth="32px" keyValueStore={contactInfo} />
														</Card>
													</div>
												</Card>
											</Col>
										</Row>
										{nullSafeGetOrElse('categoryNames.length', supplierFacility, 0) > 0 ? (
											<Row className="rowSpacing" gutter={16}>
												<Col span={24}>
													<Card>
														<h5 style={{ display: 'flex', alignItems: 'center' }}>
															Services provided
														</h5>
														<div
															style={{
																wordBreak: 'normal',
																whiteSpace: 'normal',
																fontSize: 16,
															}}
														>
															{supplierFacility.categoryNames &&
																supplierFacility.categoryNames
																	.split(',')
																	.map((categoryName) => capitalizeEachWord(categoryName))
																	.join(', ')}
														</div>
													</Card>
												</Col>
											</Row>
										) : null}
										<Row className="rowSpacing" gutter={16}>
											<Col span={24}>
												<Card>
													<h5 style={{ display: 'flex', alignItems: 'center' }}>Business hours</h5>
													<div style={{ fontSize: 16 }}>
														{operatingHours.timeZoneName ? (
															<div
																style={{
																	color: 'rgba(0,0,0,0.45)',
																	marginBottom: 16,
																}}
															>
																{operatingHours.timeZoneName}
															</div>
														) : null}
														<table>
															{DAYS_OF_THE_WEEK.map((dayOfTheWeek, idx) => {
																const fromTime = nullSafeGet(
																	`${dayOfTheWeek}.from`,
																	operatingHours
																);
																const toTime = nullSafeGet(`${dayOfTheWeek}.to`, operatingHours);
																const hasHours = fromTime && toTime;

																return (
																	<tr key={idx} style={{ lineHeight: '32px' }}>
																		<td>{dayOfTheWeek}</td>
																		<td style={{ paddingLeft: 32 }}>
																			{hasHours ? `${fromTime} to ${toTime}` : 'No hours available'}
																		</td>
																	</tr>
																);
															})}
														</table>
													</div>
												</Card>
											</Col>
										</Row>
									</Col>
								</Row>
							</Col>
						</Row>
					</div>
				)}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	supplierFacilitiesFetching: state.supplier_facilities.fetching,
	match: ownProps.match,
	history: ownProps.history,
	userType: (state as any).session.userType,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchSupplierRelationship: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? ContractorPrivateNetworksRestCrudThunksForSupplier.read({ supplierFacilityId: id })
				: ContractorPrivateNetworksRestCrudThunksForBuyer.read({ supplierFacilityId: id })
		),
	fetchSupplierFacility: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readOne(id)
				: supplierFacilitiesRestCrudThunksForBuyer.readOne(id)
		),
	requestFiles: (data) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestFilesV2ForSupplier(data)
				: requestFilesV2ForBuyer(data)
		),

	updateSupplierInPrivateNetworkV2: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? ContractorPrivateNetworksRestCrudThunksForSupplier.update(entity)
				: ContractorPrivateNetworksRestCrudThunksForBuyer.update(entity)
		),

	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
	clearNavbar: () => dispatch(clearNavbarState()),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ContractorSuppliersDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
